import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Zap, Cpu } from 'lucide-react';
export function PreFlightInterception() {
  return <section className="py-24 px-6 md:px-12 lg:px-24 bg-black text-white" data-id="element-1167">
      <div className="max-w-7xl mx-auto" data-id="element-1168">
        {/* Header */}
        <div className="mb-16 text-center" data-id="element-1169">
          <span className="text-sm font-bold tracking-widest uppercase text-[#0066FF] mb-4 block font-sans" data-id="element-1170">
            How It Works
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-8 font-['Syne']" data-id="element-1171">
            What do we mean by Pre-flight?
          </h2>
          <div className="max-w-4xl mx-auto space-y-6" data-id="element-1172">
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-1173">
              Pre-flight is the moment before an AI model begins reasoning.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-1174">
  <strong data-id="element-1175">Your enterprise should own everything before the model thinks.</strong>  That moment is where rules, behaviors, judgment,
  and risk are determined. Once the model starts reasoning, it has
  already made assumptions. Governance after the fact can only react
  to outcomes, not control how those outcomes were formed.
</p>
            <p className="text-xl text-white font-semibold leading-relaxed" data-id="element-1176">
              Pre-flight is the AI control plane.
            </p>
          </div>
        </div>

        {/* How Pre-flight works in Synthia */}
        <div className="mb-16" data-id="element-1177">
          <h3 className="text-2xl md:text-3xl font-bold mb-12 font-['Syne'] text-white text-center" data-id="element-1178">
            How Pre-flight works in Synthia
          </h3>

          {/* Three Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1179">
            {/* CMP Box */}
            <div className="relative group" data-id="element-1180">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF]/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" data-id="element-1181"></div>
              <div className="relative bg-gray-900 border-2 border-[#0066FF]/50 rounded-2xl p-8 h-full hover:border-[#0066FF] transition-all duration-300 hover:transform hover:scale-105" data-id="element-1182">
                <div className="flex flex-col items-center text-center h-full" data-id="element-1183">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0066FF] to-blue-700 flex items-center justify-center mb-6 shadow-lg shadow-[#0066FF]/50" data-id="element-1184">
                    <Database className="w-8 h-8 text-white" data-id="element-1185" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-[#0066FF] font-['Syne']" data-id="element-1186">
                    The Context Management Platform (CMP)
                  </h4>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-1187">
                    is where enterprises decide and manage how AI is allowed to
                    think. Roles, regulatory obligations, brand standards,
                    behavioral expectations, and non-negotiables are defined as
                    durable, versioned, governed assets. This is the system of
                    record for enterprise reasoning.
                  </p>
                </div>
              </div>
            </div>

            {/* MCP Box */}
            <div className="relative group" data-id="element-1188">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF]/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" data-id="element-1189"></div>
              <div className="relative bg-gray-900 border-2 border-[#0066FF]/50 rounded-2xl p-8 h-full hover:border-[#0066FF] transition-all duration-300 hover:transform hover:scale-105" data-id="element-1190">
                <div className="flex flex-col items-center text-center h-full" data-id="element-1191">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0066FF] to-blue-700 flex items-center justify-center mb-6 shadow-lg shadow-[#0066FF]/50" data-id="element-1192">
                    <Zap className="w-8 h-8 text-white" data-id="element-1193" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-[#0066FF] font-['Syne']" data-id="element-1194">
                    The Model Context Protocol (MCP)
                  </h4>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-1195">
                    is the pre-flight execution layer. It retrieves the relevant
                    context from the CMP, interprets role and intent, compiles
                    the applicable rules, and delivers a precise, governed
                    instruction set to the model before generation begins.
                    Nothing is improvised or left to chance.
                  </p>
                </div>
              </div>
            </div>

            {/* LLM Box */}
            <div className="relative group" data-id="element-1196">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF]/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" data-id="element-1197"></div>
              <div className="relative bg-gray-900 border-2 border-[#0066FF]/50 rounded-2xl p-8 h-full hover:border-[#0066FF] transition-all duration-300 hover:transform hover:scale-105" data-id="element-1198">
                <div className="flex flex-col items-center text-center h-full" data-id="element-1199">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0066FF] to-blue-700 flex items-center justify-center mb-6 shadow-lg shadow-[#0066FF]/50" data-id="element-1200">
                    <Cpu className="w-8 h-8 text-white" data-id="element-1201" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-[#0066FF] font-['Syne']" data-id="element-1202">
                    The LLM
                  </h4>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-1203">
                    is simply the engine. It does not decide how to think. It
                    executes the thinking it has been given—guided, constrained,
                    and aligned with enterprise intent.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Pre-flight matters */}
        <div className="mb-12 max-w-4xl mx-auto" data-id="element-1204">
          <h3 className="text-2xl md:text-3xl font-bold mb-6 font-['Syne'] text-white text-center" data-id="element-1205">
            Why Pre-flight matters
          </h3>
          <p className="text-lg text-gray-300 leading-relaxed mb-6 text-center" data-id="element-1206">
            Pre-flight turns AI from a reactive system into a governed one. It
            prevents hallucination, drift, and non-compliance by ensuring the
            model never reasons outside approved boundaries. It replaces
            post-hoc review with upstream control. And it allows enterprises to
            scale AI safely because governance is enforced by design, not by
            memory or manual checks.
          </p>
          <p className="text-xl text-white font-semibold leading-relaxed text-center" data-id="element-1207">
            Control the thinking first. Everything downstream gets better.
          </p>
        </div>

        {/* CTA Button */}
        <div className="mt-12 text-center" data-id="element-1208">
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0066FF] text-white font-['Syne'] font-bold text-sm tracking-wide uppercase hover:bg-white hover:text-[#0066FF] transition-all duration-200 hover:scale-105" data-id="element-1209">
            BOOK A DEMO
            <ArrowRight className="w-5 h-5" data-id="element-1210" />
          </Link>
        </div>
      </div>
    </section>;
}